import type { AdminFlowDefinition, AdminFlowNode, AdminFlowNodeKind } from "../models/admin-flows.models";
import { ADMIN_FLOW_TOOLS_PER_NODE } from "./admin-flow-whiteboard.constants";
import { ADMIN_FLOW_NODE_LABELS } from "./admin-flows.constants";

export const createAdminFlowNode = (kind: AdminFlowNodeKind, index: number): AdminFlowNode => ({
  id: crypto.randomUUID(),
  kind,
  label: ADMIN_FLOW_NODE_LABELS[kind],
  x: 50 + (index % 3) * 260,
  y: 60 + Math.floor(index / 3) * 150,
  source: kind === "source" ? "vessels" : "",
  toolName: "",
  ...(kind === "agent" ? { toolNames: [] } : {}),
  arguments: {},
  instruction: "",
  recipients: "",
  subject: "",
});

// Saved single-tool drafts remain editable; an explicit empty array means prompt-only processing.
export const getAdminFlowToolNames = (node: AdminFlowNode): string[] =>
  node.toolNames ?? (node.toolName ? [node.toolName] : []);

// Start blank rather than manufacturing successful runs or examples that look like live business data.
export const createAdminFlow = (): AdminFlowDefinition => ({
  id: crypto.randomUUID(),
  name: "Untitled flow",
  nodes: [],
  edges: [],
  status: "draft",
  revision: 1,
  schedule: { enabled: false, cron: "0 7 * * 1-5", timezone: "Europe/Zurich" },
});

export const orderAdminFlowNodes = (flow: AdminFlowDefinition): AdminFlowNode[] => {
  const nodeIds = new Set(flow.nodes.map((node) => node.id));

  if (nodeIds.size !== flow.nodes.length) throw new Error("Every block needs a unique identifier.");
  if (flow.edges.some((edge) => !nodeIds.has(edge.from) || !nodeIds.has(edge.to)))
    throw new Error("Every connection must refer to existing blocks.");

  const pending = [...flow.nodes];
  const ordered: AdminFlowNode[] = [];
  const completed = new Set<string>();

  while (pending.length) {
    const index = pending.findIndex((node) =>
      flow.edges.filter((edge) => edge.to === node.id).every((edge) => completed.has(edge.from)),
    );

    if (index < 0) throw new Error("Connections must form a flow without cycles or missing blocks.");

    const [node] = pending.splice(index, 1);
    ordered.push(node);
    completed.add(node.id);
  }

  return ordered;
};

export const connectAdminFlowNodes = (flow: AdminFlowDefinition, from: string, to: string): AdminFlowDefinition => {
  const source = flow.nodes.find((node) => node.id === from);
  const target = flow.nodes.find((node) => node.id === to);

  if (!source || !target || from === to) throw new Error("Connect two different blocks.");
  if (source.kind === "text" || source.kind === "email") throw new Error("Outputs must be the final blocks.");
  if (target.kind === "source") throw new Error("Data sources are starting points.");
  if (flow.edges.some((edge) => edge.from === from && edge.to === to))
    throw new Error("These blocks are already connected.");

  const next = { ...flow, edges: [...flow.edges, { id: crypto.randomUUID(), from, to }] };
  orderAdminFlowNodes(next);

  return next;
};

export const validateAdminFlow = (flow: AdminFlowDefinition): string[] => {
  const errors: string[] = [];

  if (!flow.name.trim()) errors.push("Give the flow a name.");
  if (!flow.nodes.length) errors.push("Add a data source or AI step to begin.");
  if (flow.nodes.length > 24) errors.push("Use at most 24 blocks per flow.");
  if (flow.edges.length > 64) errors.push("Use at most 64 connections per flow.");
  if (!flow.nodes.some((node) => node.kind === "text" || node.kind === "email"))
    errors.push("Add a text output or email draft.");

  try {
    orderAdminFlowNodes(flow);
  } catch (error) {
    errors.push(error instanceof Error ? error.message : "The flow connections are invalid.");
  }

  flow.nodes.forEach((node) => {
    if (!node.label.trim()) errors.push("Every block needs a label.");
    if (node.kind !== "source" && !flow.edges.some((edge) => edge.to === node.id))
      errors.push(`${node.label}: connect an input.`);
    if (node.kind === "source" && !["vessels", "documents", "support", "offline_tech_form"].includes(node.source))
      errors.push(`${node.label}: select an approved source.`);
    if (node.kind === "agent" && !node.instruction.trim()) errors.push(`${node.label}: add instructions.`);
    if (
      node.kind === "agent" &&
      (getAdminFlowToolNames(node).length > ADMIN_FLOW_TOOLS_PER_NODE ||
        new Set(getAdminFlowToolNames(node)).size !== getAdminFlowToolNames(node).length)
    )
      errors.push(`${node.label}: attach up to ${ADMIN_FLOW_TOOLS_PER_NODE} different tools.`);
    if (
      node.kind === "source" &&
      node.source === "documents" &&
      (node.arguments.view === undefined || node.arguments.view === "metadata") &&
      (typeof node.arguments.vesselCode !== "string" ||
        !node.arguments.vesselCode.trim() ||
        !/^\d{7}$/.test(String(node.arguments.imo ?? "")))
    )
      errors.push(`${node.label}: document metadata needs a vesselCode and seven-digit imo.`);
    if (node.kind === "email" && (!node.recipients.trim() || !node.subject.trim()))
      errors.push(`${node.label}: add recipients and a subject.`);
    if (!["text", "email"].includes(node.kind) && !flow.edges.some((edge) => edge.from === node.id))
      errors.push(`${node.label}: connect it to the next block.`);
  });

  return Array.from(new Set(errors));
};

// Narrow every generated entry before reading it; valid JSON can still contain nulls, arrays and invalid fields.
const isAdminFlowProposalObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isAdminFlowProposalKind = (value: unknown): value is AdminFlowNodeKind =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(ADMIN_FLOW_NODE_LABELS, value);

export const readAdminFlowProposal = (text: string, current: AdminFlowDefinition): AdminFlowDefinition => {
  if (text.length > 256000) throw new Error("The proposed flow is too large. Ask for a smaller flow.");

  const raw = text
    .trim()
    .replace(/^```(?:json)?\s*/, "")
    .replace(/\s*```$/, "");
  const proposal: unknown = JSON.parse(raw);

  if (!isAdminFlowProposalObject(proposal))
    throw new Error("The assistant did not return a flow object. Ask it to correct the proposal.");

  const candidate = proposal;

  if (
    !Array.isArray(candidate.nodes) ||
    !Array.isArray(candidate.edges) ||
    candidate.nodes.length > 24 ||
    candidate.edges.length > 64
  )
    throw new Error("The proposal needs bounded nodes and connections.");

  const proposedNodes: unknown[] = candidate.nodes;
  const proposedEdges: unknown[] = candidate.edges;
  const nodes = proposedNodes.map((entry, index): AdminFlowNode => {
    if (
      !isAdminFlowProposalObject(entry) ||
      !isAdminFlowProposalKind(entry.kind) ||
      typeof entry.id !== "string" ||
      !/^[a-zA-Z0-9_-]{1,100}$/.test(entry.id)
    )
      throw new Error("The proposal contains an unsupported block or identifier.");

    const defaults = createAdminFlowNode(entry.kind, index);
    const fields = ["label", "source", "toolName", "instruction", "recipients", "subject"] as const;
    const node = { ...defaults, id: entry.id };
    fields.forEach((field) => {
      const value = entry[field];

      if (value !== undefined && typeof value !== "string") throw new Error(`The proposed ${field} must be text.`);
      if (typeof value === "string") node[field] = value.slice(0, 12000);
    });

    if (entry.kind === "agent") {
      if (
        entry.toolNames !== undefined &&
        (!Array.isArray(entry.toolNames) ||
          entry.toolNames.length > ADMIN_FLOW_TOOLS_PER_NODE ||
          entry.toolNames.some((name) => typeof name !== "string" || !name.trim() || name.length > 100))
      ) {
        throw new Error(`Attach up to ${ADMIN_FLOW_TOOLS_PER_NODE} named tools per AI step.`);
      }

      node.toolNames =
        entry.toolNames === undefined ? (node.toolName ? [node.toolName] : []) : (entry.toolNames as string[]);
      if (new Set(node.toolNames).size !== node.toolNames.length)
        throw new Error("Attach each tool only once per AI step.");
      node.toolName = node.toolNames[0] ?? "";
    }

    if (typeof entry.x === "number" && Number.isFinite(entry.x)) node.x = Math.max(0, Math.min(entry.x, 4000));
    if (typeof entry.y === "number" && Number.isFinite(entry.y)) node.y = Math.max(0, Math.min(entry.y, 4000));
    if (entry.arguments !== undefined) {
      if (!isAdminFlowProposalObject(entry.arguments))
        throw new Error("Proposed tool arguments must be a JSON object.");
      if (JSON.stringify(entry.arguments).length > 24000) throw new Error("The proposed tool arguments are too large.");

      node.arguments = entry.arguments;
    }

    return node;
  });

  if (new Set(nodes.map((node) => node.id)).size !== nodes.length)
    throw new Error("Every proposed block needs a unique identifier.");

  let next: AdminFlowDefinition = {
    ...current,
    name: typeof candidate.name === "string" ? candidate.name.slice(0, 120) : current.name,
    status: "draft",
    nodes,
    edges: [],
  };
  proposedEdges.forEach((edge) => {
    if (!isAdminFlowProposalObject(edge) || typeof edge.from !== "string" || typeof edge.to !== "string")
      throw new Error("Every proposed connection needs two block identifiers.");

    next = connectAdminFlowNodes(next, edge.from, edge.to);
  });

  return next;
};
